import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, GitCompareArrows, ChevronDown, TrendingUp, TrendingDown, Minus, CheckCircle2, Brain } from 'lucide-react';

const FEEDBACK_FIELDS = [
  { key: 'effective_strategies', label: 'Most Effective Strategies', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  { key: 'recommendations_next', label: 'Recommendations', color: 'bg-blue-50 border-blue-200 text-blue-800' },
  { key: 'thinking_moment', label: 'Thinking Moment', color: 'bg-violet-50 border-violet-200 text-violet-800' },
  { key: 'reasoning_strategy', label: 'Reasoning Strategy', color: 'bg-amber-50 border-amber-200 text-amber-800' },
  { key: 'parent_note', label: 'Parent Note', color: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
  { key: 'additional_observations', label: 'Observations', color: 'bg-slate-50 border-slate-200 text-slate-700' },
];

function TrendIcon({ current, previous }) {
  if (previous === undefined || previous === null) return null;
  if (current > previous) return <TrendingUp className="w-4 h-4 text-emerald-500" />;
  if (current < previous) return <TrendingDown className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-slate-400" />;
}

function ScoreBar({ value, max, label, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs mb-1">
        <span className="font-bold text-slate-600">{label}</span>
        <span className="font-bold" style={{ color }}>{value}/{max}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function SessionComparePage() {
  const { childId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [child, setChild] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [childRes, sessRes] = await Promise.all([
          api.get(`/children/${childId}`),
          api.get(`/sessions?child_id=${childId}`),
        ]);
        setChild(childRes.data);
        const completed = sessRes.data.filter(s => s.status === 'completed').sort((a, b) => a.session_number - b.session_number);
        setSessions(completed);
        // Pre-select last 2 sessions
        if (completed.length >= 2) {
          setSelected([completed[completed.length - 2].session_id, completed[completed.length - 1].session_id]);
        } else if (completed.length === 1) {
          setSelected([completed[0].session_id]);
        }
        // Fetch assessments for all completed sessions
        const assessRes = await api.get(`/assessments?child_id=${childId}`);
        const map = {};
        assessRes.data.forEach(a => { map[a.session_id] = a; });
        setAssessments(map);
      } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [childId]);

  const toggleSession = (sid) => {
    setSelected(prev => {
      if (prev.includes(sid)) return prev.filter(id => id !== sid);
      if (prev.length >= 3) { toast.error('Max 3 sessions'); return prev; }
      return [...prev, sid];
    });
  };

  const selectedSessions = sessions.filter(s => selected.includes(s.session_id)).sort((a, b) => a.session_number - b.session_number);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-[#22D3EE]" /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="session-compare-page">
      <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 rounded-full text-slate-500 hover:text-[#0E7490]">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex items-center gap-3 mb-6">
        <GitCompareArrows className="w-7 h-7 text-[#22D3EE]" />
        <div>
          <h1 className="text-2xl font-extrabold text-[#1E1B4B]" style={{ fontFamily: 'Nunito' }}>Session Comparison</h1>
          <p className="text-sm text-slate-500">{child?.name} — Select up to 3 sessions to compare</p>
        </div>
      </div>

      {/* Session Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {sessions.map(s => (
          <button
            key={s.session_id}
            onClick={() => toggleSession(s.session_id)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
              selected.includes(s.session_id)
                ? 'bg-[#1E1B4B] text-white shadow-lg'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`compare-select-${s.session_id}`}
          >
            Session #{s.session_number}
          </button>
        ))}
      </div>

      {selectedSessions.length === 0 && (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="p-12 text-center">
            <p className="text-slate-400 text-lg">Select sessions above to compare</p>
          </CardContent>
        </Card>
      )}

      {selectedSessions.length > 0 && (
        <>
          {/* Scores Comparison */}
          <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="scores-comparison">
            <CardContent className="p-6">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Assessment Scores</h2>
              <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${selectedSessions.length}, 1fr)` }}>
                {selectedSessions.map((s, idx) => {
                  const a = assessments[s.session_id] || {};
                  const prevA = idx > 0 ? (assessments[selectedSessions[idx - 1].session_id] || {}) : null;
                  const skills = s.skills || [];
                  const completed = skills.filter(sk => sk.completed).length;
                  return (
                    <div key={s.session_id} className="p-4 rounded-2xl bg-slate-50">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-[#1E1B4B] text-white rounded-full font-bold">#{s.session_number}</Badge>
                        <span className="text-xs text-slate-400">{new Date(s.date || s.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <ScoreBar value={a.intervention_total || 0} max={40} label="Intervention" color="#0E7490" />
                        {prevA && <TrendIcon current={a.intervention_total || 0} previous={prevA.intervention_total} />}
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <ScoreBar value={a.critical_thinking_total || 0} max={5} label="Critical Thinking" color="#7C3AED" />
                        {prevA && <TrendIcon current={a.critical_thinking_total || 0} previous={prevA.critical_thinking_total} />}
                      </div>
                      <div className="flex items-center gap-2">
                        <ScoreBar value={completed} max={skills.length} label="Skills Completed" color="#059669" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feedback Comparison */}
          <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="feedback-comparison">
            <CardContent className="p-6">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Instructor Feedback</h2>
              {FEEDBACK_FIELDS.map(({ key, label, color }) => {
                const hasAny = selectedSessions.some(s => s.session_feedback?.[key]);
                if (!hasAny) return null;
                return (
                  <div key={key} className="mb-4">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{label}</p>
                    <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${selectedSessions.length}, 1fr)` }}>
                      {selectedSessions.map(s => (
                        <div key={s.session_id} className={`p-3 rounded-xl border text-sm ${color}`}>
                          <Badge variant="outline" className="mb-1 rounded-full text-[10px] px-1.5 py-0">#{s.session_number}</Badge>
                          <p className="text-xs whitespace-pre-wrap mt-1">{s.session_feedback?.[key] || <span className="italic opacity-50">Not recorded</span>}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Skills Comparison */}
          <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="skills-comparison">
            <CardContent className="p-6">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Skills Completed</h2>
              <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${selectedSessions.length}, 1fr)` }}>
                {selectedSessions.map(s => (
                  <div key={s.session_id} className="space-y-1">
                    <Badge className="bg-[#1E1B4B] text-white rounded-full font-bold mb-2">#{s.session_number}</Badge>
                    {(s.skills || []).map(sk => (
                      <div key={sk.skill_id} className={`flex items-center gap-2 text-xs p-1.5 rounded-lg ${sk.completed ? 'bg-green-50 text-green-700' : 'bg-slate-50 text-slate-400'}`}>
                        {sk.completed ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> : <div className="w-3.5 h-3.5 rounded-full border border-slate-300" />}
                        {sk.name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
